import React from "react";
import {
  Badge,
  Box,
  Button,
  Icon,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  HStack,
  VStack,
  Text,
  Th,
  Tooltip,
} from "@chakra-ui/react";
import { FiCpu, FiUser } from "react-icons/fi";
import { confirmationWindowWarning } from "../../utils/confirmationScheduling";

// C and R are terminal: the patient answered. U covers both "asked, no
// reply" and "never asked" — the spec calls for three letters, and the
// footer line below is what distinguishes those two cases.
const CONFIRM_STYLES = {
  C: { colorScheme: "green", label: "C", title: "Confirmed" },
  R: { colorScheme: "orange", label: "R", title: "Reschedule requested" },
  U: { colorScheme: "yellow", label: "U", title: "Awaiting reply" },
};

export const confirmationStyleFor = (status) =>
  CONFIRM_STYLES[String(status || "").toUpperCase()] || CONFIRM_STYLES.U;

// Distinguishes an automated status change (patient SMS reply, reminder
// sweep) from a manual staff override. Returns null for rows with no
// recorded source (legacy rows predating this field) — no icon, no guess.
const SOURCE_TOOLTIPS = {
  sms: "Confirmed automatically via SMS reply",
  email: "Answered by the patient from the confirmation email",
  system: "Marked automatically by reminder system",
};

export const sourceMetaFor = (source, updatedBy) => {
  if (!source) return null;
  if (source === "manual") {
    return {
      kind: "human",
      icon: FiUser,
      color: "#8B5E3C",
      tooltip: `Manually set by ${updatedBy || "staff"}`,
    };
  }
  return {
    kind: "ai",
    icon: FiCpu,
    color: "teal.500",
    tooltip: SOURCE_TOOLTIPS[source] || "Set automatically",
  };
};

const shortDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : `${d.getMonth() + 1}/${d.getDate()}`;
};

const ConfirmStatusCell = ({
  row,
  canEdit = false,
  onViewMessages,
  onSetStatus,
  autoStatus,
  windowDays,
}) => {
  const style = confirmationStyleFor(row?.confirmation_status);
  const asked = shortDate(row?.confirmation_sent_at);
  const replied =
    row?.confirmation_status && ["sms", "email"].includes(row?.confirmation_source)
      ? shortDate(row?.confirmation_updated_at)
      : "";
  const sourceMeta = sourceMetaFor(
    row?.confirmation_source,
    row?.confirmation_updated_by,
  );
  // "none_in_window": automatic sending is on but every rule's moment has
  // already passed for this appointment, so nothing will be queued for it.
  const showNoAuto = autoStatus === "none_in_window";
  const warning = confirmationWindowWarning(windowDays);

  return (
    <HStack spacing={1} justify="center">
      <Popover placement="bottom" isLazy>
        <PopoverTrigger>
          {/* The Box is the popover trigger and carries the row-click guard;
              a stopPropagation on the inner Button would swallow the click
              before the trigger saw it and the popover would never open. */}
          <Box
            position="relative"
            display="inline-block"
            onClick={(e) => e.stopPropagation()}
          >
            <Button size="sm" colorScheme={style.colorScheme} aria-label={style.title}>
              {style.label}
            </Button>
            {sourceMeta && (
              <Tooltip label={sourceMeta.tooltip} placement="top" hasArrow>
                <Box
                  position="absolute"
                  bottom="-4px"
                  right="-4px"
                  width="16px"
                  height="16px"
                  borderRadius="full"
                  bg="white"
                  border="1px solid"
                  borderColor="gray.200"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Icon
                    as={sourceMeta.icon}
                    boxSize="10px"
                    color={sourceMeta.color}
                  />
                </Box>
              </Tooltip>
            )}
          </Box>
        </PopoverTrigger>
        <PopoverContent width="auto" onClick={(e) => e.stopPropagation()}>
          <PopoverArrow />
          <PopoverBody>
            <VStack align="stretch" spacing={2}>
              <Text fontSize="sm" fontWeight="semibold">
                {style.title}
              </Text>
              {onViewMessages && (
                <Button
                  size="xs"
                  variant="outline"
                  onClick={() => onViewMessages(row)}
                >
                  View messages
                </Button>
              )}
              {canEdit && onSetStatus && (
                <HStack spacing={1}>
                  {["C", "R", "U"].map((s) => (
                    <Button
                      key={s}
                      size="xs"
                      colorScheme={CONFIRM_STYLES[s].colorScheme}
                      variant={style.label === s ? "solid" : "outline"}
                      aria-label={`Set ${s}`}
                      onClick={() => onSetStatus(row.id, s)}
                    >
                      {s}
                    </Button>
                  ))}
                </HStack>
              )}
              <Text fontSize="xs" color="gray.500">
                {asked ? `Asked ${asked}` : "Not yet asked"}
                {replied ? ` · Replied ${replied}` : ""}
              </Text>
              {sourceMeta && (
                <Text fontSize="xs" color="gray.500">
                  {sourceMeta.tooltip}
                </Text>
              )}
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Popover>
      {showNoAuto && (
        <Tooltip label={warning} placement="top" hasArrow>
          <Badge
            colorScheme="orange"
            variant="subtle"
            fontSize="0.6em"
            data-testid="no-auto-marker"
            aria-label={warning}
            onClick={(e) => e.stopPropagation()}
          >
            no auto
          </Badge>
        </Tooltip>
      )}
    </HStack>
  );
};

const STATUS_ORDER = ["C", "R", "U"];

// Tallies the currently-rendered rows by status, split by who/what set it.
// "auto" covers any non-manual, non-empty source (sms, system, future
// channels); "unknown" is a status set before confirmation_source existed.
export const summarizeConfirmationStatuses = (rows) => {
  const byStatus = {};
  for (const key of STATUS_ORDER) {
    byStatus[key] = { total: 0, manual: 0, auto: 0, unknown: 0 };
  }
  let notAsked = 0;
  for (const row of rows || []) {
    const status = String(row?.confirmation_status || "").toUpperCase();
    const bucket = byStatus[status];
    if (!bucket) {
      notAsked += 1;
      continue;
    }
    bucket.total += 1;
    const source = row?.confirmation_source;
    if (source === "manual") bucket.manual += 1;
    else if (source) bucket.auto += 1;
    else bucket.unknown += 1;
  }
  return { byStatus, notAsked, total: (rows || []).length };
};

const TotalsRow = ({ statusKey, bucket }) => {
  const style = CONFIRM_STYLES[statusKey];
  const parts = [];
  if (bucket.auto) parts.push(`${bucket.auto} auto`);
  if (bucket.manual) parts.push(`${bucket.manual} manual`);
  if (bucket.unknown) parts.push(`${bucket.unknown} unclassified`);
  return (
    <HStack justify="space-between" fontSize="xs" spacing={4}>
      <HStack spacing={2}>
        <Badge colorScheme={style.colorScheme}>{statusKey}</Badge>
        <Text color="gray.600">{style.title}</Text>
      </HStack>
      <Text color="gray.600" textAlign="right">
        {bucket.total}
        {parts.length ? ` (${parts.join(" · ")})` : ""}
      </Text>
    </HStack>
  );
};

// Clickable "Status" column header: opens a popover with totals per status,
// broken down by automated vs. manual, over whatever rows are currently
// rendered in the table (not a separate server-side aggregate).
export const ConfirmStatusHeaderCell = ({ rows, ...thProps }) => {
  const { byStatus, notAsked, total } = summarizeConfirmationStatuses(rows);
  return (
    <Popover placement="bottom" isLazy>
      <PopoverTrigger>
        <Th
          color="white"
          width="auto"
          py={2}
          px={2}
          textAlign="center"
          cursor="pointer"
          userSelect="none"
          {...thProps}
        >
          Status
        </Th>
      </PopoverTrigger>
      <PopoverContent width="260px" onClick={(e) => e.stopPropagation()}>
        <PopoverArrow />
        <PopoverBody>
          <VStack align="stretch" spacing={2}>
            <Text fontSize="sm" fontWeight="semibold">
              Status totals ({total})
            </Text>
            {STATUS_ORDER.map((key) => (
              <TotalsRow key={key} statusKey={key} bucket={byStatus[key]} />
            ))}
            {notAsked > 0 && (
              <HStack justify="space-between" fontSize="xs" spacing={4}>
                <Text color="gray.600">Not yet asked</Text>
                <Text color="gray.600">{notAsked}</Text>
              </HStack>
            )}
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export default ConfirmStatusCell;
